import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AdminPanel } from './AdminPanel';
import { adImageUrl, api, ApiError, pageImageUrl, loadSession, setSession, setUnauthorizedHandler } from './api';
import { Calendar } from './Calendar';
import { Clips } from './Clips';
import { InfoMenu } from './InfoMenu';
import { longDate, shortDate, todayIso } from './dates';
import { IconBolt, IconCalendar, IconGrid, IconImage, IconKey, IconLogout, IconAdvert, IconUpload } from './Icons';
import brandUrl from './Images/brand.jpg';
import { Login } from './Login';
import { PublicClip, PublicShare } from './Public';
import { Reader } from './Reader';
import { ChangeCodeDialog } from './ChangeCode';
import { navigate, useRoute } from './router';
import { useToast } from './Toast';
import { UploadDialog } from './UploadDialog';
import { PaperInfo, Session, EditionMap } from './types';
import { AdBanner, AdDialog } from './AdBanner';
import { FlashDialog, FlashTicker } from './FlashNews';
import { EditionsDialog } from './EditionsDialog';
import { BannerAds, LogoBanner } from './LogoBanner';
import { Modal } from './Modal';
import { Viewers } from './Viewers';
import { PageNav } from './PageNav';
import { PageSlides } from './PageSlides';

/** "/" -> open page 1 of today's paper. If today's paper is not there yet, say so kindly and offer the upload (and the newest paper). */
function Landing({ onEmpty }: { onEmpty: () => void }) {
  const [state, setState] = useState<{ today: string; newest: string | null } | null>(null);
  useEffect(() => {
    let live = true;
    api
      .latest()
      .then(async (r) => {
        let todays = null;
        try {
          todays = await api.paper(r.today);
        } catch {
          todays = null;
        }
        if (!live) return;
        if (todays) navigate(`/p/${r.today}/1`, true);
        else setState({ today: r.today, newest: r.paper ? r.paper.date : null });
      })
      .catch(() => live && setState({ today: todayIso(), newest: null }));
    return () => {
      live = false;
    };
  }, []);
  if (!state) return <p className="muted center pad">Opening today's paper…</p>;
  return (
    <div className="empty">
      <h2>Today's paper is not available yet</h2>
      <p>The newspaper for {longDate(state.today)} has not been uploaded. If you have today's PDF, you can add it now.</p>
      <div className="row center-row">
        <button className="btn primary" onClick={onEmpty}>
          <IconUpload size={16} /> Upload today's paper
        </button>
        {state.newest && (
          <button className="btn" onClick={() => navigate(`/p/${state.newest}/1`)}>
            Read the latest paper ({shortDate(state.newest)})
          </button>
        )}
      </div>
    </div>
  );
}

type PaperState = { state: 'loading' | 'ok' | 'missing' | 'error'; paper: PaperInfo | null; msg: string };

/** Loads the paper of a day (null date = nothing to load). */
function usePaper(date: string | null, version: number): PaperState {
  const [st, setSt] = useState<PaperState>({ state: 'loading', paper: null, msg: '' });
  useEffect(() => {
    if (!date) return;
    let live = true;
    setSt((o) => (o.paper && o.paper.date === date ? o : { state: 'loading', paper: null, msg: '' }));
    api
      .paper(date)
      .then((p) => live && setSt({ state: p ? 'ok' : 'missing', paper: p, msg: '' }))
      .catch((e) => live && setSt({ state: 'error', paper: null, msg: e instanceof ApiError ? e.message : 'Could not open the paper.' }));
    return () => {
      live = false;
    };
  }, [date, version]);
  return st;
}

function PaperView({ date, page, st, adVersion, nav, after, onUpload }: { date: string; page: number; st: PaperState; adVersion: number; nav: (curPage: number) => React.ReactNode; after: React.ReactNode; onUpload: (d: string) => void }) {
  if (st.state === 'loading') return <p className="muted center pad">Loading…</p>;
  if (st.state === 'error') return <p className="error center pad">{st.msg}</p>;
  const paper = st.paper;
  if (st.state === 'missing' || !paper)
    return (
      <div className="empty">
        <h2>No newspaper for this day</h2>
        <p>{longDate(date)} has not been uploaded. If you have the PDF, you can add it now – missed days can be uploaded any time.</p>
        <button className="btn primary" onClick={() => onUpload(date)}>
          <IconUpload size={16} /> Upload for this date
        </button>
      </div>
    );
  const p = Math.min(Math.max(1, page || 1), paper.pageCount);
  return (
    <Reader
      source={{ kind: 'auth', date }}
      pageCount={paper.pageCount}
      page={p}
      onPage={(n) => navigate(`/p/${date}/${n}`, true)}
      nav={nav(p)}
      after={after}
      top={p === 1 ? <AdBanner date={date} version={adVersion} /> : null}
      onFix={() => onUpload(date)}
    />
  );
}

function Shell({ session, route, onLogout }: { session: Session; route: string; onLogout: () => void }) {
  const toast = useToast();
  const [version, setVersion] = useState(0);
  const [adVersion, setAdVersion] = useState(0);
  const [uploadFor, setUploadFor] = useState<string | null>(null);
  const [archives, setArchives] = useState(false);
  const [slidesOpen, setSlidesOpen] = useState(false);
  const [changeCode, setChangeCode] = useState(false);
  const [adFor, setAdFor] = useState<string | null>(null);
  const [flashOpen, setFlashOpen] = useState(false);
  const [flashVersion, setFlashVersion] = useState(0);
  const [editionsFor, setEditionsFor] = useState(false);
  const [editions, setEditions] = useState<EditionMap>({});
  const [edition, setEdition] = useState('Main');

  // a link shared from the reader (…?share=1) opens without the top bar, and stays that way while turning pages
  const [bare, setBare] = useState(() => /[?&]share=1/.test(route));
  useEffect(() => {
    if (/[?&]share=1/.test(route)) setBare(true);
    else if (!route.startsWith('/p/')) setBare(false);
  }, [route]);

  const m = route.match(/^\/p\/(\d{4}-\d{2}-\d{2})(?:\/(\d+))?(?:\?.*)?$/);
  const date = m ? m[1] : null;
  const page = m && m[2] ? parseInt(m[2], 10) : 1;
  // a link opened from the reader (?share=1) shows the day's adverts beside the logo too
  const [ads, setAds] = useState<BannerAds | undefined>(undefined);
  useEffect(() => {
    if (!bare || !date) { setAds(undefined); return; }
    let live = true;
    api.ads(date).then((list) => {
      if (!live) return;
      const f = (n: number) => { const a = list.find((x) => x.slot === n); return a ? adImageUrl(a) : undefined; };
      setAds({ left: f(1), right: f(2) });
    }).catch(() => live && setAds({}));
    return () => { live = false; };
  }, [bare, date, version]);
  const isAdmin = session.role === 'ADMIN';
  const st = usePaper(date, version);
  const bareVideo = useMemo(() => (bare && date && st.state === 'ok' && st.paper ? { date, urls: Array.from({ length: st.paper.pageCount }, (_, i) => pageImageUrl(date, i + 1, 400)) } : null), [bare, date, st]);
  const paper = date && st.state === 'ok' ? st.paper : null;

  // district pages belong to one day's paper
  useEffect(() => {
    setEdition('Main');
    setEditions({});
    if (!date) return;
    let live = true;
    api
      .editions(date)
      .then((e) => live && setEditions(e))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [date, version]);

  const pick = useCallback((d: string, p: PaperInfo | null) => {
    setArchives(false);
    if (p) navigate(`/p/${d}/1`);
    else setUploadFor(d);
  }, []);

  const go = (n: number, ed?: string) => {
    if (!date) return;
    if (ed) setEdition(ed);
    setSlidesOpen(false);
    navigate(`/p/${date}/${n}`, true);
  };

  let body: React.ReactNode;
  if (date) body = (
      <PaperView
        date={date}
        page={page}
        st={st}
        adVersion={adVersion}
        onUpload={setUploadFor}
        after={
          <>
            <button className="cbtn tone-rose" onClick={() => setArchives(true)} title="Archive – older papers">
              <IconCalendar size={18} /> <span>Archive</span>
            </button>
            <InfoMenu />
          </>
        }
        nav={(cur) =>
          paper && (
            <PageNav
              date={date}
              pageCount={paper.pageCount}
              page={cur}
              editions={editions}
              edition={edition}
              isAdmin={isAdmin}
              onGo={go}
              onNoDistrict={(d) => toast(`${d} pages are not marked for this day's paper yet.`, 'info')}
              onEditEditions={() => setEditionsFor(true)}
            />
          )
        }
      />
    );
  else if (route === '/clips') body = <Clips />;
  else if (route === '/admin' && isAdmin) body = <AdminPanel onPapersChanged={() => setVersion((v) => v + 1)} onSignOut={onLogout} />;
  else body = <Landing onEmpty={() => setUploadFor(todayIso())} />;

  const readerView = !!date;
  const curPage = paper ? Math.min(Math.max(1, page || 1), paper.pageCount) : 1;

  return (
    <div className="app">
      {bare && <LogoBanner ads={ads} video={bareVideo} page={page} />}
      {!bare && (
      <header className="topbar">
        <a className="brand" href="#/" aria-label="Royal Journalism home">
          <img className="brand-logo" src={brandUrl} alt="Royal Journalism" />
          <span className="brand-text">Royal Journalism</span>
        </a>
        <div className="top-mid">
          <div className="top-date">{date ? longDate(date) : ''}</div>
          <Viewers date={date || ''} isAdmin={isAdmin} />
        </div>
        <nav className="top-actions">
          {paper && (
            <button className="cbtn tone-slate show-narrow" onClick={() => setSlidesOpen(true)} aria-label="Show all pages">
              <IconGrid size={16} /> <span>Pages</span>
            </button>
          )}
          <button className="cbtn tone-sky" onClick={() => setUploadFor(date || todayIso())} title="Upload a newspaper PDF" aria-label="Upload">
            <IconUpload size={16} /> <span>Upload</span>
          </button>
          <button className="cbtn tone-orange" onClick={() => setAdFor(date || todayIso())} title="Add an advertisement above page 1">
            <IconAdvert size={16} /> <span>Advert</span>
          </button>
          <button className="cbtn tone-red" onClick={() => setFlashOpen(true)} title="Flash news – the scrolling headlines" aria-label="Flash news">
            <IconBolt size={16} /> <span>Flash</span>
          </button>
          <a className="cbtn tone-green" href="#/clips" title="Clippings" aria-label="Clippings" onClick={(e) => { e.preventDefault(); navigate('/clips'); }}>
            <IconImage size={16} /> <span>Clippings</span>
          </a>
          {isAdmin && (
            <a className="cbtn tone-pink" href="#/admin" title="Admin" aria-label="Admin" onClick={(e) => { e.preventDefault(); navigate('/admin'); }}>
              <IconKey size={16} /> <span>Admin</span>
            </a>
          )}
          {!isAdmin && (
            <button className="cbtn tone-brown" onClick={() => setChangeCode(true)} title="Choose your own code">
              <IconKey size={16} /> <span>My code</span>
            </button>
          )}
          <button className="cbtn tone-slate" onClick={onLogout} title={'Signed in as ' + session.name}>
            <IconLogout size={16} /> <span>{session.name.split(' ')[0]}</span>
          </button>
        </nav>
      </header>
      )}
      <FlashTicker version={flashVersion} />

      <div className={'layout' + (paper ? ' with-slides' : '')}>
        {paper && date && (
          <aside className={'side' + (slidesOpen ? ' open' : '')} aria-label="Pages">
            <button className="drawer-close show-narrow" onClick={() => setSlidesOpen(false)} aria-label="Close pages">
              ×
            </button>
            <PageSlides date={date} pageCount={paper.pageCount} page={curPage} onGo={(n) => go(n)} />
          </aside>
        )}
        {slidesOpen && <div className="scrim show-narrow" onClick={() => setSlidesOpen(false)} />}
        <main className={'content' + (readerView ? ' reading' : '')}>
          {!paper && (
            <div className="subbar">
              <button className="cbtn tone-rose" onClick={() => setArchives(true)} title="Archive – older papers">
                <IconCalendar size={16} /> <span>Archive</span>
              </button>
              <InfoMenu />
            </div>
          )}
          {body}
        </main>
      </div>

      {archives && (
        <Modal title="Archive" onClose={() => setArchives(false)}>
          <Calendar selected={date} version={version} onPick={pick} />
        </Modal>
      )}
      {changeCode && <ChangeCodeDialog onClose={() => setChangeCode(false)} />}
      {flashOpen && <FlashDialog onClose={() => setFlashOpen(false)} onChanged={() => setFlashVersion((v) => v + 1)} />}
      <FlashTicker version={flashVersion} slot={2} />
      {adFor && <AdDialog date={adFor} onClose={() => setAdFor(null)} onChanged={() => setAdVersion((v) => v + 1)} />}
      {editionsFor && date && paper && (
        <EditionsDialog date={date} pageCount={paper.pageCount} current={editions} onClose={() => setEditionsFor(false)} onSaved={setEditions} />
      )}

      {uploadFor && (
        <UploadDialog
          initialDate={uploadFor}
          isAdmin={isAdmin}
          onClose={() => setUploadFor(null)}
          onDeleted={(d) => {
            setVersion((v) => v + 1);
            toast(`Newspaper for ${longDate(d)} deleted. You can upload it again now.`, 'ok');
          }}
          onDone={(p) => {
            setUploadFor(null);
            setVersion((v) => v + 1);
            toast(`Newspaper for ${longDate(p.date)} saved (${p.pageCount} pages)`, 'ok');
            navigate(`/p/${p.date}/1`);
          }}
        />
      )}
    </div>
  );
}

export default function App() {
  const route = useRoute();
  const toast = useToast();
  const [session, setSess] = useState<Session | null>(() => loadSession());

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setSession(null);
      setSess(null);
      toast('Your session ended. Please sign in again.', 'info');
    });
  }, [toast]);

  if (route.startsWith('/s/')) return <PublicShare token={route.slice(3).split('/')[0]} />;
  if (route.startsWith('/c/')) return <PublicClip token={route.slice(3).split('/')[0]} />;
  if (!session) return <Login onDone={setSess} />;
  return (
    <Shell
      session={session}
      route={route}
      onLogout={() => {
        setSession(null);
        setSess(null);
        navigate('/', true);
      }}
    />
  );
}
